import styles from './ContextMenu.module.scss';
import { Text, Spacer, FloatingMenu } from '@components';
import { useContext, useRef } from 'react';
import { ContextMenuContext } from './ContextMenuProvider';
import { useContextMenu } from '@hooks';
import { TContextMenuItem, TContextMenuDivider } from '@types';
import MoreIcon from '@mui/icons-material/MoreVertOutlined';

type TProps<T> = TContextMenuItem<T> | TContextMenuDivider;

export const Item = <T, >(props: TProps<T>) => {
	const { icon, label, onClick, disabled = false, visible = true, subMenu = [] } = (props as TContextMenuItem<T>);
	const { divider } = (props as TContextMenuDivider);
	const { closeContext } = useContextMenu();
	const ctx = useContext(ContextMenuContext);
	const btnRef = useRef<HTMLButtonElement>(null);
	const isDisabled = () => {
		if (typeof disabled === 'boolean') return disabled;
		if (typeof disabled === 'function') return disabled(ctx.data);
		return false;
	}
	const isVisible = () => {
		if (typeof visible === 'boolean') return visible;
		if (typeof visible === 'function') return visible(ctx.data);
		return true;
	}
	const menuClick = () => {
		if (!isDisabled() && subMenu.length === 0) {
			onClick(ctx.data);
			closeContext(ctx.id);
		}
	}
	const isSubMenuDisabled = (item: TContextMenuItem<T>) => {
		if (typeof item.disabled === 'boolean') return item.disabled;
		if (typeof item.disabled === 'function') return item.disabled(ctx.data);
		return false;
	}
	const isSubMenuVisible = (item: TContextMenuItem<T>) => {
		if (typeof item.visible === 'boolean') return item.visible;
		if (typeof item.visible === 'function') return item.visible(ctx.data);
		return true;
	}
	const subMenuClick = (item: TContextMenuItem<T>) => {
		if (!isSubMenuDisabled(item)) {
			item.onClick(ctx.data);
			closeContext(ctx.id);
		}
	}
	const getPosition = () => {
		if (btnRef && btnRef.current) {
			const styles = ctx.getStyles(true);
			const { width } = btnRef.current.getBoundingClientRect();
			const windowWidth = window.innerWidth;
			if ((styles as Record<string, number>).x + width + 200 > windowWidth) {
				return 'full-left'
			} else {
				return 'full-right';
			}
		}
		return 'full-right';
	}
	return (
		<>
			{!divider && isVisible() && <FloatingMenu>
				<button
					className={`${styles.menuItem} ${isDisabled() ? styles.disabled : ''}`}
					onClick={menuClick}
					ref={btnRef}
				>
					{icon}
					<div className={styles.fullWidthText}>
						<Text>{label}</Text>
					</div>
					{subMenu.length > 0 && <MoreIcon />}
				</button>
				<FloatingMenu.Menu menuPos={getPosition()}>
					{subMenu.filter((item) => {
						return isSubMenuVisible(item);
					}).map((item, i) => {
						return <button
							key={i}
							className={`${styles.menuItem} ${isSubMenuDisabled(item) ? styles.disabled : ''}`}
							onClick={() => subMenuClick(item)}
						>
							{item.icon}
							<Text>{item.label}</Text>
						</button>
					})}
				</FloatingMenu.Menu>
			</FloatingMenu>}
			{divider && <Spacer size={0} divider fill />}
		</>
	);
}
